import React, { useContext } from "react";
import { useMemo, useRef, useState } from "react";
import { GlobalContext } from "../../providers/GlobalProvider";
import { toast } from "./lib/toast";
import { Upload, Download, Check, ChevronLeft, ChevronRight } from "./icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  cx,
} from "./ui";
import {
  bulkUploadImportProducts,
  waitForImportProductJob,
  type ImportProductJobState,
} from "./lib/import-service";
import {
  parseCsvText,
  autoMap,
  templateColumnsForStructure,
  buildProductImportMetadata,
  downloadTemplate,
  getUploadRowCount,
  type TemplateColumn,
} from "./CreateImportDatabaseDialog";
import {
  checkProductCredits,
  type ProductCreditCheck,
} from "./lib/product-credits";
import { ProductCreditsModal } from "./ProductCreditsModal";

type Step = "upload" | "map" | "import" | "done";

/**
 * Bulk-imports products into an EXISTING database (Slice B's "Import Data").
 * Shares the canonical product template and server-side header mapping with
 * CreateImportDatabaseDialog. Only a bounded preview is read in the browser;
 * the original file is streamed by the product worker.
 */
export const BulkCsvUploadDialog: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  databaseId: string;
  databaseStructure?: 'hierarchy' | 'flat' | 'hts-code-grouping';
  /** Called once the import request is accepted (products import asynchronously after). */
  onImported?: () => void;
  /** When supplied, shows a "Back" affordance on the upload step that
   * returns to the caller's method chooser instead of just closing. */
  onBack?: () => void;
}> = ({
  open,
  onOpenChange,
  databaseId,
  databaseStructure,
  onImported,
  onBack,
}) => {
  // Hub-level Add Products doesn't know the chosen database's structure, so it
  // offers both templates; mapping keeps the hierarchy columns as before.
  const structure = databaseStructure ?? 'hierarchy';
  const [step, setStep] = useState<Step>("upload");
  const [filename, setFilename] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<number, TemplateColumn | ''>>({});
  const [saving, setSaving] = useState(false);
  const [importJob, setImportJob] = useState<ImportProductJobState | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [rejectType, setRejectType] = useState(false);
  const [creditBlock, setCreditBlock] = useState<ProductCreditCheck | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const pollGenerationRef = useRef(0);
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;

  const reset = () => {
    setStep("upload");
    setFilename("");
    setFile(null);
    setHeaders([]);
    setRows([]);
    setMapping({});
    setSaving(false);
    setImportJob(null);
    setDragActive(false);
    setRejectType(false);
    setCreditBlock(null);
    pollGenerationRef.current += 1;
  };
  const close = () => {
    pollGenerationRef.current += 1;
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  const onFile = async (file: File) => {
    const text = await file.slice(0, 1024 * 1024).text();
    const { headers: h, rows: r } = parseCsvText(text);
    if (!h.length) { toast.error("Empty file"); return; }
    setHeaders(h);
    setRows(r.slice(0, 100));
    setFile(file);
    setFilename(file.name);
    setMapping(autoMap(h, undefined, templateColumnsForStructure(structure)));
    setStep("map");
  };

  // The file input's `accept=".csv"` only filters the OS picker, not a drop —
  // dropped files need their own extension check.
  const acceptDroppedFile = async (f: File) => {
    if (!/\.csv$/i.test(f.name)) {
      setRejectType(true);
      toast.error("Unsupported file type — CSV only.");
      setTimeout(() => setRejectType(false), 2200);
      return;
    }
    await onFile(f);
  };

  const nameColIdx = useMemo(() => {
    const entry = Object.entries(mapping).find(([, v]) => v === 'name');
    return entry ? Number(entry[0]) : -1;
  }, [mapping]);
  const doImport = async () => {
    if (nameColIdx < 0) {
      toast.warning('Map a product name column before importing.');
      return;
    }
    if (!file) {
      toast.warning('Choose a CSV before importing.');
      return;
    }
    setSaving(true);
    const generation = pollGenerationRef.current + 1;
    pollGenerationRef.current = generation;
    try {
      if (groupId) {
        const rowCount = await getUploadRowCount(file);
        const check = await checkProductCredits(groupId, rowCount);
        if (!check.ok) {
          setCreditBlock(check);
          return;
        }
      }

      const { resolvedHeaders, notMappedColumns } = buildProductImportMetadata(
        headers,
        mapping,
      );
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('fileType', 'csv');
      formData.append('import_source', 'csv_xlsx');
      formData.append('streamingImport', 'true');
      formData.append('database_id', databaseId);
      formData.append('resolvedHeaders', JSON.stringify(resolvedHeaders));
      formData.append('notMappedColumns', JSON.stringify(notMappedColumns));

      const { success, error, jobId } = await bulkUploadImportProducts(formData);
      if (!success) {
        toast.error(error ?? 'Failed to import products');
        return;
      }
      setStep("done");
      toast.success('Product import queued');

      if (!jobId) {
        onImported?.();
        return;
      }

      const completedJob = await waitForImportProductJob(jobId, {
        isCancelled: () => pollGenerationRef.current !== generation,
        onProgress: setImportJob,
      });
      if (!completedJob) return;
      if (completedJob.status === 'completed') {
          toast.success(
            `${completedJob.insertedRows} product${
              completedJob.insertedRows === 1 ? '' : 's'
            } imported`,
          );
          onImported?.();
          return;
      }
      toast.error(completedJob.error ?? 'Product import failed');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to monitor import',
      );
    } finally {
      if (pollGenerationRef.current === generation) setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent size="lg" onClose={close}>
        <DialogHeader>
          <div className="ih-dialog-icon-header">
            <span className="ih-dialog-icon-badge">
              <Upload size={20} />
            </span>
            <div className="ih-dialog-icon-copy">
              <DialogTitle>Bulk upload — CSV</DialogTitle>
              <p className="ih-dialog-subtitle">
                Add products to this database from a CSV file.
              </p>
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          <StepBar step={step} />

          {step === "upload" && (
            <div className="ih-col ih-gap-3 ih-mt-3">
              <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) void acceptDroppedFile(f);
                }}
                className={cx(
                  "ih-dropzone",
                  dragActive && "is-active",
                  rejectType && "is-reject",
                )}
              >
                <Upload size={32} className="ih-text-muted" />
                <div className="ih-mt-2 ih-font-medium ih-text-sm">Click to upload a CSV</div>
                <div className="ih-text-xs ih-text-muted">or drag and drop</div>
                {rejectType && (
                  <div className="ih-mt-1 ih-text-xs" style={{ color: "var(--danger)" }}>
                    Unsupported file type — CSV only.
                  </div>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv"
                  className="ih-hidden"
                  onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
                />
              </div>
              {databaseStructure ? (
                <button
                  type="button"
                  onClick={() => downloadTemplate(databaseStructure)}
                  className="ih-create-db-secondary-button"
                  style={{ alignSelf: 'center' }}
                >
                  <Download size={18} /> Download CSV Template
                </button>
              ) : (
                <div className="ih-row-wrap ih-gap-2" style={{ justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => downloadTemplate('flat')}
                    className="ih-create-db-secondary-button"
                  >
                    <Download size={18} /> Download Flat List Template
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadTemplate('hierarchy')}
                    className="ih-create-db-secondary-button"
                  >
                    <Download size={18} /> Download BOM Template
                  </button>
                </div>
              )}
            </div>
          )}

          {step === "map" && (
            <div className="ih-col ih-gap-3 ih-mt-3">
              <div className="ih-text-xs ih-text-muted">
                {filename} · previewing {rows.length} rows
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  overflowY: 'auto',
                  maxHeight: 280,
                }}
              >
                {headers.map((h, i) => {
                  const used = new Set(
                    Object.entries(mapping)
                      .filter(([k]) => Number(k) !== i)
                      .map(([, v]) => v)
                      .filter(Boolean),
                  );
                  return (
                    <div key={i} className="ih-row ih-gap-2" style={{ alignItems: 'center' }}>
                      <div
                        title={h}
                        className="ih-text-xs ih-font-semibold"
                        style={{
                          flex: '0 0 38%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h || <span className="ih-text-muted">(unnamed)</span>}
                      </div>
                      <span className="ih-text-muted ih-text-xs">→</span>
                      <select
                        value={mapping[i] ?? ''}
                        onChange={(e) =>
                          setMapping((prev) => ({
                            ...prev,
                            [i]: e.target.value as TemplateColumn | '',
                          }))
                        }
                        className="ih-select"
                        style={{ flex: 1 }}
                      >
                        <option value=''>Not mapped</option>
                        {templateColumnsForStructure(structure).map((c) => (
                          <option key={c} value={c} disabled={used.has(c) && mapping[i] !== c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              <div className="ih-kicker ih-mt-2">Preview (first 3 rows)</div>
              <div className="ih-subsurface" style={{ overflowX: "auto" }}>
                <table className="ih-text-xs" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} className="ih-preview-th">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 3).map((r, i) => (
                      <tr key={i}>
                        {r.map((c, j) => (
                          <td key={j} className="ih-preview-td">{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="ih-success-block">
              <Check size={48} className="ih-tone-success" />
              <div className="ih-font-semibold" style={{ fontSize: 16 }}>Import started</div>
              <p className="ih-text-sm ih-text-muted">
                {importJob
                  ? `${
                      importJob.status === 'completed' ? 'Imported' : 'Processed'
                    } ${importJob.processedRows} of ${importJob.totalRows} rows · ${
                      importJob.insertedRows
                    } added · ${importJob.duplicateRows} duplicates · ${
                      importJob.invalidRows
                    } invalid.`
                  : 'Your file is queued and will be imported in the background.'}
              </p>
              <ImportHierarchyWarnings job={importJob} />
              <Button variant="primary" onClick={close}>Done</Button>
            </div>
          )}
        </DialogBody>
        {step === "upload" && onBack && (
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={onBack}
              style={{ marginRight: "auto" }}
            >
              <ChevronLeft size={14} /> Back to method chooser
            </Button>
          </DialogFooter>
        )}
        {step === "map" && (
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setStep("upload")}
              style={{ marginRight: "auto" }}
              disabled={saving}
            >
              <ChevronLeft size={14} /> Back
            </Button>
            <Button
              variant="primary"
              onClick={doImport}
              disabled={saving || nameColIdx < 0}
            >
              {saving ? 'Uploading…' : 'Import products'} <ChevronRight size={14} />
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
    <ProductCreditsModal
      check={creditBlock}
      teamId={groupId ?? ''}
      onClose={() => setCreditBlock(null)}
    />
    </>
  );
}

const ImportHierarchyWarnings: React.FC<{
  job: ImportProductJobState | null;
}> = ({ job }) => {
  const warnings = job?.warnings ?? [];
  if (!warnings.length) return null;
  return (
    <div className="ih-subsurface ih-text-xs" style={{ textAlign: "left", width: "100%" }}>
      <div className="ih-font-semibold ih-tone-warning">
        {warnings.length} parent link{warnings.length === 1 ? "" : "s"} could not be created
      </div>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18, maxHeight: 140, overflowY: "auto" }}>
        {warnings.map((warning, index) => (
          <li key={`${warning.row}-${index}`}>
            Row {warning.row}: {warning.message}
          </li>
        ))}
      </ul>
    </div>
  );
};

const StepBar: React.FC<{ step: Step }> = ({ step }) => {
  const ALL: Step[] = ["upload", "map", "done"];
  const i = ALL.indexOf(step === "import" ? "map" : step);
  return (
    <div className="ih-row-wrap ih-gap-2 ih-text-xs ih-text-muted">
      {ALL.map((s, idx) => (
        <div key={s} className="ih-row ih-gap-1">
          <span className={cx("ih-stepbar-dot", idx <= i && "is-active")} />
          <span className={cx("ih-capitalize", idx === i && "ih-font-semibold", idx === i && "ih-text-foreground")}>
            {s}
          </span>
        </div>
      ))}
    </div>
  );
}
